import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { adversaries as allAdversaries } from '../content/adversaries';
import type { Adversary } from '../content/adversaries';
import { fuzzyFilter } from '../search/fuzzySearch';
import { parseTextWithTerms } from '../character/content/parseText';
import { useStrings } from '../locales';
import { SortIcon, SortState } from '../components/SortIcon';
import { useNewSourceFilter } from '../hooks/useNewSourceFilter';
import { StatBox, StatBrowser, StatFeature } from '../components/StatBlock';
import './Adversaries.css';



interface FilterChip {
  label: string;
  color?: string;
  onRemove: () => void;
}

// ── Colour maps ─────────────────────────────────────────────────────
const ROLE_COLORS: Record<string, string> = {
  Minion: '#888888',
  Standard: '#5a9ebd',
  Support: '#a06bdd',
  Ranged: '#60b8d8',
  Skulk: '#9060c8',
  Social: '#c9a227',
  Bruiser: '#c87840',
  Leader: '#c9a227',
  Solo: '#d47840',
  Horde: '#c8c8c8',
};

const TIER_COLORS: Record<number, string> = { 1: '#a89cc8', 2: '#60d890', 3: '#e8c547', 4: '#f06ab0' };

const ALL_ROLES = ['All', 'Minion', 'Standard', 'Ranged', 'Skulk', 'Support', 'Social', 'Bruiser', 'Leader', 'Solo', 'Horde'];
const ALL_TIERS = ['All', '1', '2', '3', '4'];

// Sort cols are built dynamically in the component using strings

const DEFAULT_SORT: SortState = { col: 'tier', dir: 'asc' };

// ── Multi-select helpers ─────────────────────────────────────────────
function toggleFilter(current: Set<string>, value: string): Set<string> {
  if (value === 'All') return new Set(['All']);
  const next = new Set(current);
  next.delete('All');
  if (next.has(value)) {
    next.delete(value);
    if (next.size === 0) next.add('All');
  } else {
    next.add(value);
  }
  return next;
}
function passesFilter(set: Set<string>, value: string): boolean { return set.has('All') || set.has(value); }
function isDefault(s: Set<string>): boolean { return s.size === 1 && s.has('All'); }

// ── Sort helpers ─────────────────────────────────────────────────────
function sortAdversaries(list: Adversary[], sort: SortState): Adversary[] {
  if (!sort.col) return list;
  const d = sort.dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (sort.col) {
      case 'name': return d * a.name.localeCompare(b.name);
      case 'role': return d * (a.role || '').localeCompare(b.role || '') || a.name.localeCompare(b.name);
      case 'difficulty': return d * (Number(a.difficulty) - Number(b.difficulty)) || a.name.localeCompare(b.name);
      case 'hp': return d * (Number(a.hp) - Number(b.hp)) || a.name.localeCompare(b.name);
      case 'tier': return d * (a.tier - b.tier) || a.name.localeCompare(b.name);
      default: return 0;
    }
  });
}

// ── Sort icon ────────────────────────────────────────────────────────

// ── Reset button ─────────────────────────────────────────────────────
interface ResetBtnProps { dirty: boolean; onClick: () => void; title: string; label: string; }
function ResetBtn({ dirty, onClick, title, label }: ResetBtnProps) {
  return (
    <button
      className={`reset-btn${dirty ? ' dirty' : ''}`}
      onClick={onClick}
      disabled={!dirty}
      title={title}
    >
      <RotateCcw size={13} /> {label}
    </button>
  );
}

// ── Stat card, laid out like the SRD stat block ─────────────────────
const roleLabelOf = (adv: Adversary) => (adv.roleSubtype ? `${adv.role} (${adv.roleSubtype})` : adv.role);

function AdvStatCard({ adv }: { adv: Adversary }) {
  const s = useStrings().adversaries;
  return (
    <article className="srdcard">
      <h2 className="srdcard__name">{adv.name}</h2>
      <p className="srdcard__kind">{`${s.tierLabel(adv.tier)} ${roleLabelOf(adv)}`}</p>
      <p className="srdcard__desc">{parseTextWithTerms(adv.description)}</p>
      <p className="statblock__line"><strong>{s.motivesLabel}</strong> {parseTextWithTerms(adv.motives)}</p>
      <StatBox
        rows={[
          [
            { label: s.stats.difficulty, value: adv.difficulty },
            { label: s.stats.thresholds, value: adv.thresholds ? `${adv.thresholds.major}/${adv.thresholds.severe}` : s.stats.none },
            { label: s.stats.hp, value: adv.hp },
            { label: s.stats.stress, value: adv.stress ?? s.stats.none },
          ],
          [
            { label: s.stats.atk, value: adv.atk.modifier },
            { label: adv.atk.name, value: adv.atk.range },
            { value: adv.atk.damage },
          ],
        ]}
        footer={adv.experience && <><strong>{s.stats.experience}:</strong> {adv.experience}</>}
      />
      {adv.features && adv.features.length > 0 && (
        <>
          <h3 className="srdcard__features">{s.featuresTitle}</h3>
          {adv.features.map((f, i) => <StatFeature key={i} feature={f} />)}
        </>
      )}
      <span className="source-label">{adv.source === 'void' ? 'Void' : 'Core'}</span>
    </article>
  );
}

// ════════════════════════════════════════════════════════════════
//   Main page
// ════════════════════════════════════════════════════════════════
export default function Adversaries() {
  const s = useStrings().adversaries;
  const SORT_COLS = [
    { col: 'tier',       label: s.sortCols.tier },
    { col: 'name',       label: s.sortCols.name },
    { col: 'role',       label: s.sortCols.role },
    { col: 'difficulty', label: s.sortCols.difficulty },
    { col: 'hp',         label: s.sortCols.hp },
  ];
  const [searchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [roleFilter, setRoleFilter] = useState<Set<string>>(new Set(['All']));
  const [tierFilter, setTierFilter] = useState<Set<string>>(new Set(['All']));
  const [sourceFilter, setSourceFilter] = useState<string>('All');
  const [search, setSearch] = useState<string>(searchParams.get('q') || '');
  const { glows, onSourceClick } = useNewSourceFilter('/adversaries');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearch(q);
  }, [searchParams]);

  const handleSort = (col: string) =>
    setSort((prev) => prev.col === col
      ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' }
    );

  const isSortDirty = sort.col !== 'tier' || sort.dir !== 'asc';
  const dirty = !!search || !isDefault(roleFilter) || !isDefault(tierFilter) || sourceFilter !== 'All' || isSortDirty;

  const reset = () => {
    setSearch(''); setRoleFilter(new Set(['All']));
    setTierFilter(new Set(['All'])); setSourceFilter('All'); setSort(DEFAULT_SORT);
  };

  const filtered = useMemo(() => {
    const base = allAdversaries.filter((a) => {
      if (!passesFilter(roleFilter, a.role)) return false;
      if (!passesFilter(tierFilter, String(a.tier))) return false;
      if (sourceFilter === 'Void') return a.source === 'void';
      if (sourceFilter === 'Core') return !a.source;
      return true;
    });
    return fuzzyFilter(base, search,
      (a) => [a.description, a.motives, ...(a.features || [])].join(' '),
      (a) => a.name
    );
  }, [roleFilter, tierFilter, sourceFilter, search]);

  const sorted = useMemo(
    () => search.trim() ? filtered : sortAdversaries(filtered, sort),
    [filtered, sort, search]
  );

  const useTierGroups = sort.col === 'tier' && !search;
  const tierOrder = sort.dir === 'asc' ? [1, 2, 3, 4] : [4, 3, 2, 1];

  const chips: FilterChip[] = [
    ...(search ? [{ label: `"${search}"`, color: 'var(--gold)', onRemove: () => setSearch('') }] : []),
    ...[...roleFilter].filter(v => v !== 'All').map(v => ({ label: v, color: ROLE_COLORS[v], onRemove: () => setRoleFilter(p => toggleFilter(p, v)) })),
    ...[...tierFilter].filter(v => v !== 'All').map(v => ({ label: `T${v}`, color: TIER_COLORS[Number(v)], onRemove: () => setTierFilter(p => toggleFilter(p, v)) })),
    ...(sourceFilter !== 'All' ? [{ label: sourceFilter, color: 'var(--text-muted)', onRemove: () => setSourceFilter('All') }] : []),
  ];

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>{s.title}</h1>
        <p>{s.desc}</p>
      </div>

      <input
        type="text"
        id="adversaries-search"
        name="adversaries-search"
        aria-label={s.searchAria}
        placeholder={s.searchPlaceholder}
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        className="search-input filter-search-bar"
      />

      <div className="filter-layout">

        {/* Mobile: toggle + reset row */}
        <div className="filter-toggle-row">
          <button
            className="filter-toggle-btn"
            onClick={() => setShowFilters(f => !f)}
            aria-expanded={showFilters}
            aria-controls="filter-panel"
          >
            <span>⚙ Filters{dirty ? ' •' : ''}</span>
            <span>{showFilters ? '▲' : '▼'}</span>
          </button>
          <ResetBtn dirty={dirty} onClick={reset} title={s.resetTitle} label="Reset" />
        </div>

        {/* Mobile: active filter chips */}
        {chips.length > 0 && (
          <div className="mobile-filter-chips">
            {chips.map(({ label, color, onRemove }) => (
              <span key={label} className="mobile-filter-chip" style={{ '--chip-color': color || 'var(--gold)' } as React.CSSProperties}>
                {label}
                <button className="mobile-filter-chip__remove" onClick={onRemove}>×</button>
              </span>
            ))}
          </div>
        )}

        {/* ── Left panel ── */}
        <div id="filter-panel" className={`filter-sidebar filter-sidebar-panel${showFilters ? '' : ' filter-sidebar--hidden'}`}>
          <div className="filter-sidebar-panel__header">
            <span className="filter-label">Filters</span>
            <span className="hide-mobile"><ResetBtn dirty={dirty} onClick={reset} title={s.resetTitle} label="Reset" /></span>
          </div>

          <div className="filter-sidebar-panel__count" aria-live="polite" aria-atomic="true">{filtered.length} of {allAdversaries.length}</div>

          <div className="filter-sidebar-panel__section">
            <div className="filter-label filter-sidebar-panel__section-label">{s.sortCols.role}</div>
            <div className="filter-sidebar-panel__pill-group">
              {ALL_ROLES.map((r) => {
                const active = roleFilter.has(r);
                const accent = r !== 'All' ? ROLE_COLORS[r] : null;
                return (
                  <button
                    key={r}
                    onClick={() => setRoleFilter(toggleFilter(roleFilter, r))}
                    className={`sidebar-filter-btn${active ? ' active' : ''}`}
                    aria-pressed={active}
                    style={accent ? {
                      '--role-color': accent,
                      '--role-bg': `${accent}22`,
                      '--role-border': accent,
                      '--role-color-dim': `${accent}cc`,
                      '--role-border-dim': `${accent}44`,
                    } as React.CSSProperties : {}}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* ── Right panel ── */}
        <div className="filter-right-panel">

          <div className="horiz-filter-bar">
            <div className="horiz-filter-group">
              <span className="filter-label">{s.sortCols.tier}</span>
              {ALL_TIERS.map((t) => (
                <button
                  key={t}
                  className={`filter-btn horiz-filter-btn${tierFilter.has(t) ? ' active' : ''}`}
                  aria-pressed={tierFilter.has(t)}
                  onClick={() => setTierFilter(toggleFilter(tierFilter, t))}
                >
                  {t === 'All' ? 'All' : `T${t}`}
                </button>
              ))}
            </div>

            <div className="horiz-filter-divider" />

            <div className="horiz-filter-group">
              <span className="filter-label">Source</span>
              {['All', 'Core', 'Void'].map((src) => (
                <button
                  key={src}
                  className={`filter-btn horiz-filter-btn${sourceFilter === src ? ' active' : ''}${glows(src) ? ' new-source-glow' : ''}`}
                  aria-pressed={sourceFilter === src}
                  onClick={() => { setSourceFilter(src); onSourceClick(src); }}
                >
                  {src}
                </button>
              ))}
            </div>

            <div className="horiz-filter-divider" />

            <div className="horiz-filter-group">
              <span className="filter-label">Sort</span>
              {/* Sort label from filters strings */}
              {SORT_COLS.map(({ col, label }) => (
                <button
                  key={col}
                  className={`filter-btn horiz-filter-btn${sort.col === col ? ' active' : ''}`}
                  onClick={() => handleSort(col)}
                >
                  {label}<SortIcon col={col} sort={sort} />
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 && (
            <div className="empty-state" role="status"><p>{s.noMatch}</p></div>
          )}

          <StatBrowser
            groups={useTierGroups
              ? tierOrder.map((tier) => {
                const items = sorted.filter((a) => a.tier === tier);
                return { key: `tier-${tier}`, label: <><span>{s.tierLabel(tier)}</span><span>{items.length}</span></>, items };
              })
              : [{ key: 'all', items: sorted }]}
            keyOf={(adv) => adv.name}
            accentOf={(adv) => ROLE_COLORS[adv.role] || 'var(--border-light)'}
            renderItem={(adv) => (
              <>
                <span className="statview__name">{adv.name}</span>
                <span className="statview__meta">{roleLabelOf(adv)} · Diff {adv.difficulty} · HP {adv.hp}</span>
              </>
            )}
            renderCard={(adv) => <AdvStatCard adv={adv} />}
            listLabel={s.title}
            closeLabel={s.backToList}
          />
        </div>
      </div>
    </div>
  );
}
